import { z } from 'zod';
import {
  BaseResponseSchema,
} from './primitives.js';

import { LocationIntegrationStatusSchema } from './location-integration.schemas.js';
// ─── Location Overview (All-Sites) Schemas ────────────────────────────────────

export const SiteTagCountsSchema = z.object({
  total: z.number().int(),
  online: z.number().int(),
  offline: z.number().int(),
  sos: z.number().int(),
  lowBattery: z.number().int(),
});


export const SiteOverviewSchema = z.object({
  siteId: z.string(),
  displayName: z.string().nullable(),
  tagCounts: SiteTagCountsSchema,
  lastUpdatedAt: z.string().nullable(),
});


export const SiteOverviewListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    sites: z.array(z.object({
      siteId: z.string(),
      displayName: z.string().nullable(),
      tagCounts: SiteTagCountsSchema,
      lastUpdatedAt: z.string().nullable(),
      siteLat: z.number().nullable(),
      siteLng: z.number().nullable(),
      siteAddressLabel: z.string().nullable(),
      floorCount: z.number().int(),
      integrationStatus: LocationIntegrationStatusSchema,
      integrationEnabled: z.boolean(),
    })),
    total: z.number().int(),
  }),
});


// ─── Provider: Tenant Sites List ─────────────────────────────────────────────

export const ProviderTenantSitesResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    tenantId: z.string(),
    sites: z.array(z.object({
      siteId: z.string(),
      label: z.string().nullable(),
      hasIntegration: z.boolean(),
    })),
    total: z.number().int(),
  }),
});


// ─── Tag Registry (All-Tags) Schemas ─────────────────────────────────────────

export const AllTagsQuerySchema = z.object({
  siteId: z.string().optional(),
  status: z.enum(['online', 'offline', 'sos', 'lowBattery']).optional(),
  search: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});


export const AllTagsItemSchema = z.object({
  tagId: z.string(),
  siteId: z.string(),
  x: z.number().optional(),
  y: z.number().optional(),
  z: z.number().optional(),
  layerId: z.number().optional(),
  floorId: z.string().optional(),
  zoneName: z.string().optional(),
  online: z.boolean(),
  battery: z.number().nullable(),
  sosActive: z.boolean(),
  lastSeenAt: z.string(),
});


export const AllTagsResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    tags: z.array(AllTagsItemSchema),
    total: z.number().int(),
  }),
});


// ─── Site Geo-Location Schemas ────────────────────────────────────────────────

const SiteDisplayNameSchema = z.string().trim().min(1).max(128);

export const SiteGeoSchema = z.object({
  siteId: z.string(),
  displayName: z.string().nullable(),
  siteLat: z.number().min(-90).max(90).nullable(),
  siteLng: z.number().min(-180).max(180).nullable(),
  siteAddressLabel: z.string().max(256).nullable(),
});


export const SiteGeoUpdateBodySchema = z.object({
  displayName: SiteDisplayNameSchema.optional(),
  siteLat: z.number().min(-90).max(90).optional(),
  siteLng: z.number().min(-180).max(180).optional(),
  siteAddressLabel: z.string().max(256).optional(),
});


export const SiteCreateBodySchema = SiteGeoUpdateBodySchema.extend({
  displayName: SiteDisplayNameSchema,
});


export const SiteGeoResponseSchema = BaseResponseSchema.extend({
  data: SiteGeoSchema,
});


// ─── Geocoding (Nominatim proxy) ───────────────────────────────────────────────

export const NominatimSearchResultSchema = z.object({
  place_id: z.number(),
  display_name: z.string(),
  lat: z.string(),
  lon: z.string(),
});


export const GeocodingSearchQuerySchema = z.object({
  q: z.string().trim().min(3).max(200),
  limit: z.coerce.number().int().min(1).max(10).optional().default(6),
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
  countryCode: z.string().trim().length(2).optional(),
});


export const GeocodingSearchResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    results: z.array(NominatimSearchResultSchema),
  }),
});


export const GeocodingReverseQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
});


export const GeocodingReverseResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    displayName: z.string().nullable(),
  }),
});


// Extend SiteOverview to include geo fields (used in Location Overview page)
export const SiteOverviewWithGeoSchema = SiteOverviewSchema.extend({
  siteLat: z.number().nullable(),
  siteLng: z.number().nullable(),
  siteAddressLabel: z.string().nullable(),
  floorCount: z.number().int(),
});


export const SiteOverviewWithGeoListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    sites: z.array(SiteOverviewWithGeoSchema),
    total: z.number().int(),
  }),
});
// Inferred types
export type AllTagsItem = z.infer<typeof AllTagsItemSchema>;
export type AllTagsQuery = z.infer<typeof AllTagsQuerySchema>;
export type AllTagsResponse = z.infer<typeof AllTagsResponseSchema>;
export type GeocodingReverseQuery = z.infer<typeof GeocodingReverseQuerySchema>;
export type GeocodingReverseResponse = z.infer<typeof GeocodingReverseResponseSchema>;
export type GeocodingSearchQuery = z.infer<typeof GeocodingSearchQuerySchema>;
export type GeocodingSearchResponse = z.infer<typeof GeocodingSearchResponseSchema>;
export type NominatimSearchResult = z.infer<typeof NominatimSearchResultSchema>;
export type ProviderTenantSitesResponse = z.infer<typeof ProviderTenantSitesResponseSchema>;
export type SiteCreateBody = z.infer<typeof SiteCreateBodySchema>;
export type SiteGeo = z.infer<typeof SiteGeoSchema>;
export type SiteGeoResponse = z.infer<typeof SiteGeoResponseSchema>;
export type SiteGeoUpdateBody = z.infer<typeof SiteGeoUpdateBodySchema>;
export type SiteOverview = z.infer<typeof SiteOverviewSchema>;
export type SiteOverviewListResponse = z.infer<typeof SiteOverviewListResponseSchema>;
export type SiteOverviewWithGeo = z.infer<typeof SiteOverviewWithGeoSchema>;
export type SiteOverviewWithGeoListResponse = z.infer<typeof SiteOverviewWithGeoListResponseSchema>;
export type SiteTagCounts = z.infer<typeof SiteTagCountsSchema>;
